// Simulação de parcelamento no cartão com a taxa da AbacatePay paga pelo
// cliente (repasse de taxa, `card.passFeesToCustomer`). A Curral Tech recebe o
// preço cheio; o cliente paga o preço mais o acréscimo da opção escolhida.
//
// Taxas da tabela pública da AbacatePay para recebimento em D+1
// (abacatepay.com/pricing, consultada em 03/11/2026): percentual sobre o total
// da venda + R$ 0,60 fixo. Mudou a tabela ou a conta tem taxa própria? É aqui.
export const TAXA_FIXA_CENTAVOS = 60;

// Percentual por número de parcelas, em centésimos de ponto (542 = 5,42%).
export const TAXA_POR_PARCELAS: Record<number, number> = {
  1: 542,
  2: 692,
  3: 791,
  4: 891,
  5: 990,
  6: 1090,
  7: 1239,
  8: 1339,
  9: 1438,
  10: 1538,
  11: 1637,
  12: 1737,
};

// Regra da AbacatePay: parcela mínima de R$ 10.
export const PARCELA_MINIMA_CENTAVOS = 1000;

// Quem paga a taxa do cartão: 1 = cliente (repasse total, é o que o link de
// pagamento faz), 0 = Curral Tech, 0.5 = metade do acréscimo para cada lado.
export type ParteDoCliente = 1 | 0.5 | 0;

export interface OpcaoParcelamento {
  parcelas: number;
  totalCentavos: number;
  parcelaCentavos: number;
  acrescimoCentavos: number;
  taxaCentavos: number;
  recebidoCentavos: number;
}

const taxaSobre = (totalCentavos: number, parcelas: number) =>
  Math.round((totalCentavos * TAXA_POR_PARCELAS[parcelas]) / 10000) + TAXA_FIXA_CENTAVOS;

/**
 * Menor total que, descontada a taxa, ainda deixa o preço inteiro para a
 * empresa. Bate com o exemplo da documentação: R$ 100 à vista vira R$ 106,36.
 */
export function totalComTaxa(precoCentavos: number, parcelas: number): number {
  const fator = 1 - TAXA_POR_PARCELAS[parcelas] / 10000;
  let total = Math.floor((precoCentavos + TAXA_FIXA_CENTAVOS) / fator);
  while (total - taxaSobre(total, parcelas) < precoCentavos) total += 1;
  return total;
}

/**
 * Opções de 1x até o teto que o link de pagamento oferece. O teto segue a
 * regra da API (`parcelasPermitidas`): preço dividido pela parcela mínima, no
 * máximo 12. Assim a tabela nunca mostra uma opção que o checkout não tem.
 *
 * O cliente paga a parte dele do acréscimo do repasse total; a taxa da
 * AbacatePay é calculada sobre o que ele pagou e o resto é o que a empresa recebe.
 */
export function simularParcelas(
  precoCentavos: number,
  parteDoCliente: ParteDoCliente = 1
): OpcaoParcelamento[] {
  if (!(precoCentavos > 0)) return [];
  const teto = Math.max(1, Math.min(12, Math.floor(precoCentavos / PARCELA_MINIMA_CENTAVOS)));
  const opcoes: OpcaoParcelamento[] = [];
  for (let parcelas = 1; parcelas <= teto; parcelas += 1) {
    const acrescimoCheio = totalComTaxa(precoCentavos, parcelas) - precoCentavos;
    const totalCentavos = precoCentavos + Math.round(acrescimoCheio * parteDoCliente);
    const taxaCentavos = taxaSobre(totalCentavos, parcelas);
    // Arredonda a parcela para cima: o centavo que sobra fica na última.
    const parcelaCentavos = Math.ceil(totalCentavos / parcelas);
    opcoes.push({
      parcelas,
      totalCentavos,
      parcelaCentavos,
      acrescimoCentavos: totalCentavos - precoCentavos,
      taxaCentavos,
      recebidoCentavos: totalCentavos - taxaCentavos,
    });
  }
  return opcoes;
}
